import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * StickyBar — sticky action bar pinned to an edge of the viewport (or its
 * scroll container). Used for:
 *   - "Back to ___" page-level nav bars at the top.
 *   - Unsaved-changes confirmation bar at the bottom.
 *   - Selection action bar that appears when 1+ row in a table is selected.
 *
 * Position is fixed by default. Pass position='sticky' when the bar lives
 * inside a scroll container and should stick to that scroll position
 * instead of the viewport.
 *
 * Dumb component — the parent decides whether to render it at all (e.g.
 * conditional on dirty state) and provides the action handlers.
 */

export type StickyBarSide = "top" | "bottom";
export type StickyBarPosition = "fixed" | "sticky" | "static";
export type StickyBarTone = "neutral" | "primary" | "success" | "warning" | "danger";

export interface StickyBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Edge to pin to. Default 'bottom'. */
  side?: StickyBarSide;
  /** Positioning strategy. Default 'fixed'. */
  position?: StickyBarPosition;
  /** Tone — sets the bar background tint. */
  tone?: StickyBarTone;
  /** Leading slot — title / status / count. */
  leading?: React.ReactNode;
  /** Trailing slot — actions, usually a row of Buttons. */
  trailing?: React.ReactNode;
  /** Whether the bar is currently visible. When false, applies a slide-out animation. */
  visible?: boolean;
}

export const StickyBar = React.forwardRef<HTMLDivElement, StickyBarProps>(function StickyBar(
  {
    children,
    className,
    leading,
    position = "fixed",
    side = "bottom",
    tone = "neutral",
    trailing,
    visible = true,
    ...props
  },
  ref,
) {
  return (
    <div
      ref={ref}
      // biome-ignore lint/a11y/useSemanticElements: <section> only acts as a region landmark when accessibly named — sticky bars carry varied content and consumers attach their own aria-label per-instance, so we keep the explicit role="region" on a div.
      role="region"
      aria-hidden={!visible}
      className={cx(
        "cw-v2-sticky-bar",
        `cw-v2-sticky-bar--${side}`,
        `cw-v2-sticky-bar--${position}`,
        `cw-v2-sticky-bar--${tone}`,
        !visible && "cw-v2-sticky-bar--hidden",
        className,
      )}
      {...props}
    >
      <div className="cw-v2-sticky-bar__inner">
        {leading ? <div className="cw-v2-sticky-bar__leading">{leading}</div> : null}
        {children ? <div className="cw-v2-sticky-bar__body">{children}</div> : null}
        {trailing ? <div className="cw-v2-sticky-bar__trailing">{trailing}</div> : null}
      </div>
    </div>
  );
});
StickyBar.displayName = "StickyBar";
